import { UIEvent, useCallback, useRef, useState } from 'react';
import { AutoComplete } from '@components/common';
import notify from '@helpers/toastify-helper';
import { fetchCpdProductList } from '@services/quote.service';
import { IQuoteProductOption } from '../helper';
import css from './index.module.scss';

export const CPD_PRODUCT_LOAD_ERROR = "Couldn't load products. Try again.";
const PAGE_SIZE = 20;
const SCROLL_THRESHOLD_PX = 40;

interface ICpdProductResult {
  cpd_product_id: string;
  product_name: string;
  product_id: string | null;
  hs_code: string | null;
  primary_cas_number: string | null;
}

interface ICpdProductState {
  options: ICpdProductResult[];
  next: number | null;
  loading: boolean;
  error: string | null;
  loaded: boolean;
}

/**
 * Paginated, server-side searched CPD product options.
 * Every new search restarts at page 1; responses of superseded requests are ignored.
 */
export const useCpdProductOptions = () => {
  const [state, setState] = useState<ICpdProductState>({
    options: [],
    next: null,
    loading: false,
    error: null,
    loaded: false
  });
  const requestIdRef = useRef(0);
  const searchRef = useRef('');
  const stateRef = useRef(state);
  stateRef.current = state;

  const load = useCallback(async (search: string, page: number) => {
    const requestId = ++requestIdRef.current;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    const response = await fetchCpdProductList({ search, page, page_size: PAGE_SIZE });
    if (requestId !== requestIdRef.current) return;

    if (response?.success) {
      const results: ICpdProductResult[] = response?.data?.results ?? [];
      setState((prev) => ({
        options: page === 1 ? results : [...prev.options, ...results],
        next: response?.data?.next ?? null,
        loading: false,
        error: null,
        loaded: true
      }));
    } else {
      setState((prev) => ({
        ...prev,
        options: page === 1 ? [] : prev.options,
        next: null,
        loading: false,
        error: CPD_PRODUCT_LOAD_ERROR,
        loaded: true
      }));
      if (page > 1) notify({ severity: 'error', message: CPD_PRODUCT_LOAD_ERROR });
    }
  }, []);

  const search = useCallback(
    (text?: string | null) => {
      searchRef.current = text ?? '';
      load(searchRef.current, 1);
    },
    [load]
  );

  const loadMore = useCallback(() => {
    const { next, loading } = stateRef.current;
    if (next && !loading) load(searchRef.current, next);
  }, [load]);

  /** Lazily loads page 1 the first time the field is used (or retries after an error). */
  const ensureLoaded = useCallback(() => {
    const { loaded, loading, error } = stateRef.current;
    if ((!loaded || error) && !loading) search(searchRef.current);
  }, [search]);

  return { ...state, search, loadMore, ensureLoaded };
};

const isSameProduct = (option: any, value: any) => {
  if (!option || !value) return false;
  if (option.cpd_product_id && value.cpd_product_id) {
    return option.cpd_product_id === value.cpd_product_id;
  }
  return option.product_name === value.product_name;
};

interface ICpdProductAutocompleteProps {
  value: IQuoteProductOption | null;
  onChange: (product: IQuoteProductOption | null) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  rootClassName?: string;
}

/**
 * Product dropdown backed by CPD (GET quote-generation/filter-options/cpd-product/).
 * Only CPD products can be picked. An older saved product (cpd_product_id: null) is still
 * shown as the current value and is kept as-is until the user picks another product.
 */
const CpdProductAutocomplete = (props: ICpdProductAutocompleteProps) => {
  const {
    value,
    onChange,
    label,
    placeholder = 'Select/Type',
    required,
    disabled,
    error,
    helperText,
    rootClassName
  } = props;
  const {
    options,
    loading,
    error: loadError,
    search,
    loadMore,
    ensureLoaded
  } = useCpdProductOptions();

  const handleInputChange = (event: any, text: string, reason?: string) => {
    // 'reset' fires when a value is selected or set programmatically, not when typing.
    if (reason === 'reset') return;
    search(text);
  };

  const handleSelection = (event: any, option: ICpdProductResult | null) => {
    if (!option) {
      onChange(null);
      return;
    }
    onChange({
      product_id: option.product_id ?? null,
      product_name: option.product_name,
      cpd_product_id: option.cpd_product_id,
      is_new_cpd_selection: true
    });
  };

  const handleListScroll = (event: UIEvent<HTMLElement>) => {
    const list = event.currentTarget;
    if (list.scrollTop + list.clientHeight >= list.scrollHeight - SCROLL_THRESHOLD_PX) {
      loadMore();
    }
  };

  return (
    <div className={css.root} onFocusCapture={ensureLoaded} onMouseDownCapture={ensureLoaded}>
      <AutoComplete
        required={required}
        label={label}
        disabled={disabled}
        placeholder={placeholder}
        value={value ?? null}
        options={options}
        onInputChange={handleInputChange}
        onInputSelection={handleSelection}
        filterOptions={(list: ICpdProductResult[]) => list}
        isOptionEqualToValue={isSameProduct}
        keyOption="product_name"
        loading={loading}
        noOptionsText={loadError ?? (loading ? 'Loading…' : 'No products found')}
        ListboxProps={{ onScroll: handleListScroll }}
        renderOption={(optionProps: any, option: ICpdProductResult) => (
          <li {...optionProps} key={option.cpd_product_id}>
            <div className={css.option}>
              <span>{option.product_name}</span>
              {(option.hs_code || option.primary_cas_number) && (
                <span className={css.secondary}>
                  {[
                    option.hs_code && `HS ${option.hs_code}`,
                    option.primary_cas_number && `CAS ${option.primary_cas_number}`
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              )}
            </div>
          </li>
        )}
        error={error}
        helperText={helperText ?? (loadError && !options.length ? loadError : undefined)}
        rootClassName={rootClassName}
      />
    </div>
  );
};

export default CpdProductAutocomplete;
